/**
 * Validators compartidos frontend — deben estar alineados con backend/middleware/auth.js.
 * Si cambian aquí, actualizar también allá.
 */

export function validatePassword(pw) {
  const errors = [];
  if (!pw || pw.length < 8) errors.push('Al menos 8 caracteres');
  if (!/[A-Z]/.test(pw || '')) errors.push('Una mayúscula');
  if (!/[a-z]/.test(pw || '')) errors.push('Una minúscula');
  if (!/[0-9]/.test(pw || '')) errors.push('Un número');
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pw || '')) errors.push('Un carácter especial (!@#$%...)');
  return { valid: errors.length === 0, errors };
}

export function validateEmail(email) {
  if (!email) return { valid: false, error: 'Email requerido' };
  // RFC 5322 simplified (aligned with backend regex)
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  if (!re.test(email)) return { valid: false, error: 'Formato de email inválido' };
  return { valid: true };
}

// Spanish DNI/NIE validation with control letter
export function validateDNI(dni) {
  if (!dni) return { valid: true }; // optional field
  const clean = dni.toUpperCase().replace(/\s+/g, '');
  const LETTERS = 'TRWAGMYFPDXBNJZSQVHLCKE';
  // DNI: 8 digits + letter. NIE: starts with X/Y/Z → map to 0/1/2 + 7 digits + letter
  let num, letter;
  if (/^[XYZ][0-9]{7}[A-Z]$/.test(clean)) {
    const prefix = { X: '0', Y: '1', Z: '2' }[clean[0]];
    num = parseInt(prefix + clean.slice(1, 8), 10);
    letter = clean[8];
  } else if (/^[0-9]{8}[A-Z]$/.test(clean)) {
    num = parseInt(clean.slice(0, 8), 10);
    letter = clean[8];
  } else {
    return { valid: false, error: 'DNI/NIE: 8 dígitos + letra (o X/Y/Z + 7 + letra)' };
  }
  if (LETTERS[num % 23] !== letter) return { valid: false, error: 'Letra de control incorrecta' };
  return { valid: true };
}

// Spanish CIF validation (basic format + control)
export function validateCIF(cif) {
  if (!cif) return { valid: true }; // optional
  const clean = cif.toUpperCase().replace(/\s+/g, '');
  if (!/^[ABCDEFGHJNPQRSUVW][0-9]{7}[0-9A-J]$/.test(clean)) {
    return { valid: false, error: 'CIF: letra + 7 dígitos + dígito/letra' };
  }
  // Basic control check
  const digits = clean.slice(1, 8).split('').map(Number);
  const evenSum = digits[1] + digits[3] + digits[5];
  const oddSum = digits.filter((_, i) => i % 2 === 0).reduce((s, d) => {
    const x = d * 2; return s + (x > 9 ? x - 9 : x);
  }, 0);
  const total = evenSum + oddSum;
  const controlDigit = (10 - (total % 10)) % 10;
  const lastChar = clean[8];
  const letters = 'JABCDEFGHI';
  if (/[0-9]/.test(lastChar)) {
    if (parseInt(lastChar, 10) !== controlDigit) return { valid: false, error: 'Dígito de control incorrecto' };
  } else {
    if (letters[controlDigit] !== lastChar) return { valid: false, error: 'Letra de control incorrecta' };
  }
  return { valid: true };
}

export function validatePhone(phone) {
  if (!phone) return { valid: true }; // optional
  const clean = phone.replace(/[\s\-()]/g, '');
  // Spanish: +34 followed by 9 digits, or just 9 digits
  if (!/^(\+?[0-9]{1,3})?[0-9]{9}$/.test(clean)) {
    return { valid: false, error: 'Teléfono: 9 dígitos (opcional prefijo +34)' };
  }
  return { valid: true };
}

// IBAN (international, basic structure — full mod-97 check)
export function validateIBAN(iban) {
  if (!iban) return { valid: true }; // optional
  const clean = iban.toUpperCase().replace(/\s+/g, '');
  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/.test(clean)) {
    return { valid: false, error: 'IBAN: formato inválido' };
  }
  // Mod-97 check
  const rearranged = clean.slice(4) + clean.slice(0, 4);
  const numeric = rearranged.replace(/[A-Z]/g, (c) => (c.charCodeAt(0) - 55).toString());
  // BigInt for safety
  try {
    if (BigInt(numeric) % 97n !== 1n) return { valid: false, error: 'IBAN: dígito de control incorrecto' };
  } catch { return { valid: false, error: 'IBAN inválido' }; }
  return { valid: true };
}

export function validateSlug(slug) {
  if (!slug) return { valid: false, error: 'Slug requerido' };
  if (!/^[a-z0-9-]{3,30}$/.test(slug)) {
    return { valid: false, error: '3-30 caracteres: minúsculas, números, guiones' };
  }
  return { valid: true };
}

// ─── Validators "estilo string" para FormField error prop ───
// Devuelven null si OK, string con mensaje si KO. Más cómodo de combinar.
export const required = (v, name = 'Campo') => (v == null || String(v).trim() === '') ? `${name} es obligatorio` : null;
export const emailErr = (v) => v ? validateEmail(v).error || null : null;
export const cifErr   = (v) => v ? validateCIF(v).error || null : null;
export const dniErr   = (v) => v ? validateDNI(v).error || null : null;
export const ibanErr  = (v) => v ? validateIBAN(v).error || null : null;
export const phoneErr = (v) => v ? validatePhone(v).error || null : null;
export const cpErr    = (v) => v && !/^[0-9]{5}$/.test(v) ? 'CP debe ser 5 dígitos' : null;

// Helper para validar form completo. rules: { campo: [validatorFn, ...] }
// Devuelve { campo: errorMsg } solo para los que fallaron. Si vacío → form OK.
export function validateForm(values, rules) {
  const errors = {};
  for (const [field, vs] of Object.entries(rules)) {
    for (const v of vs) {
      const err = v(values[field]);
      if (err) { errors[field] = err; break; }
    }
  }
  return errors;
}

// ─── Catálogos para dropdowns frecuentes ───
export const PROVINCIAS_ES = [
  'Álava', 'Albacete', 'Alicante', 'Almería', 'Asturias', 'Ávila',
  'Badajoz', 'Baleares', 'Barcelona', 'Burgos',
  'Cáceres', 'Cádiz', 'Cantabria', 'Castellón', 'Ciudad Real', 'Córdoba', 'Cuenca',
  'Gerona', 'Granada', 'Guadalajara', 'Guipúzcoa',
  'Huelva', 'Huesca',
  'Jaén', 'La Coruña', 'La Rioja', 'Las Palmas', 'León', 'Lérida', 'Lugo',
  'Madrid', 'Málaga', 'Murcia',
  'Navarra',
  'Orense',
  'Palencia', 'Pontevedra',
  'Salamanca', 'Santa Cruz de Tenerife', 'Segovia', 'Sevilla', 'Soria',
  'Tarragona', 'Teruel', 'Toledo',
  'Valencia', 'Valladolid', 'Vizcaya',
  'Zamora', 'Zaragoza',
  'Ceuta', 'Melilla',
];
